import React, { useEffect, useState } from 'react';
import { Activity, Download, Eye, Users, Globe, Monitor, Mail, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import VisitorMap from '../components/VisitorMap';
import LeafletMap from '../components/LeafletMap';

const RANGES = [{ v: 1, l: 'Today' }, { v: 7, l: '7 days' }, { v: 30, l: '30 days' }, { v: 0, l: 'All time' }];

function MembersLocationMap() {
  const { getMemberLocations } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let tries = 0;
    let timer = null;
    let stop = false;
    const poll = () => {
      getMemberLocations()
        .then((d) => {
          if (stop) return;
          setData(d);
          setErr(false);
          setLoading(false);
          tries += 1;
          if (d && d.pending > 0 && tries < 12) timer = setTimeout(poll, 15000);
        })
        .catch(() => { if (!stop) { setErr(true); setLoading(false); } });
    };
    poll();
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [getMemberLocations]);
  const markers = (data && data.markers) ? data.markers.map((m) => ({ lat: m.lat, lng: m.lng, count: m.count, label: m.suburb })) : [];
  const pending = data && data.pending ? data.pending : 0;
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border mb-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="members-location-map">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-title text-sm" style={{ color: 'var(--navy)' }}>Where members are (by suburb)</h4>
        <span className="text-xs" style={{ color: 'var(--olive)' }}>
          {data ? `${markers.length} suburb${markers.length === 1 ? '' : 's'} · ${data.members_located || 0} members${pending ? ' · mapping more…' : ''}` : ''}
        </span>
      </div>
      {loading ? (
        <p className="text-xs" style={{ color: 'var(--olive)' }}>Mapping member suburbs…</p>
      ) : err ? (
        <p className="text-xs" style={{ color: '#8a1f11' }} data-testid="members-map-error">Couldn’t load the member map right now. Please try again in a moment.</p>
      ) : markers.length === 0 ? (
        <p className="text-xs" style={{ color: 'var(--olive)' }}>No member suburbs to map yet. Add a suburb to member records to see them here.</p>
      ) : (
        <LeafletMap markers={markers} center={[-42.0, 147.0]} zoom={7} height={420} cluster testid="members-leaflet-map" />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border flex items-center gap-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-center w-10 h-10 rounded-full" style={{ background: 'var(--navy)' }}>
        <Icon size={18} color="var(--cream)" />
      </div>
      <div>
        <div className="text-2xl font-title" style={{ color: 'var(--maroon)' }}>{value}</div>
        <div className="text-xs" style={{ color: 'var(--olive)' }}>{label}</div>
      </div>
    </div>
  );
}

function BreakdownList({ title, items }) {
  const max = Math.max(1, ...(items || []).map((i) => i.count));
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      <h4 className="font-title text-sm mb-3" style={{ color: 'var(--navy)' }}>{title}</h4>
      {(!items || items.length === 0) ? (
        <p className="text-xs" style={{ color: 'var(--olive)' }}>No data yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i}>
              <div className="flex justify-between text-xs mb-0.5" style={{ color: 'var(--ink)' }}>
                <span className="truncate pr-2" title={it.label}>{it.label}</span>
                <span style={{ color: 'var(--olive)' }}>{it.count}</span>
              </div>
              <div className="h-1.5 rounded" style={{ background: 'rgba(0,0,0,0.06)' }}>
                <div className="h-1.5 rounded" style={{ width: `${(it.count / max) * 100}%`, background: 'var(--gold)' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AnalyticsTab() {
  const { getAnalyticsSummary, getAnalyticsLog, analyticsExportUrl,
    getWeeklyDigest, updateWeeklyDigest, sendWeeklyDigestNow } = useAuth();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digest, setDigest] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestMsg, setDigestMsg] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getAnalyticsSummary(days || undefined),
      getAnalyticsLog(100, 0),
    ]).then(([s, l]) => { setSummary(s); setLog(l); }).catch(() => {}).finally(() => setLoading(false));
  }, [days, getAnalyticsSummary, getAnalyticsLog]);

  useEffect(() => { getWeeklyDigest().then(setDigest).catch(() => {}); }, [getWeeklyDigest]);

  const saveDigest = async (patch) => {
    const next = { ...digest, ...patch };
    setDigest(next);
    try { setDigest(await updateWeeklyDigest({ enabled: next.enabled, send_day: next.send_day, extra_recipients: next.extra_recipients })); }
    catch (e) { /* keep optimistic */ }
  };

  const sendDigestNow = async () => {
    setDigestBusy(true); setDigestMsg(null);
    try {
      const r = await sendWeeklyDigestNow();
      setDigestMsg({ ok: !r.mocked, text: r.mocked
        ? `Digest generated for ${r.recipients} recipient(s) but NOT sent — email provider is in Mock mode. Configure it in Email Settings.`
        : `Weekly digest sent to ${r.sent} of ${r.recipients} committee recipient(s).` });
      setDigest((d) => d ? { ...d, last_sent: new Date().toISOString() } : d);
    } catch (e) {
      setDigestMsg({ ok: false, text: (e.response && e.response.data && e.response.data.detail) || 'Could not send digest.' });
    }
    setDigestBusy(false);
  };

  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div data-testid="analytics-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Activity size={18} style={{ color: 'var(--maroon)' }} />
          <h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Site Activity</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded overflow-hidden border" style={{ borderColor: 'rgba(0,0,0,0.15)' }}>
            {RANGES.map((r) => (
              <button key={r.v} onClick={() => setDays(r.v)} data-testid={`range-${r.v}`}
                className="px-3 py-1.5 text-sm font-title"
                style={days === r.v ? { background: 'var(--navy)', color: 'var(--cream)' } : { background: 'white', color: 'var(--ink)' }}>
                {r.l}
              </button>
            ))}
          </div>
          <a href={analyticsExportUrl()} data-testid="analytics-export"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm font-title"
            style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
            <Download size={15} /> Export CSV
          </a>
        </div>
      </div>

      {digest && (
        <div className="bg-white rounded-lg p-4 shadow-sm border mb-5" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="weekly-digest-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h4 className="flex items-center gap-2 font-title text-sm" style={{ color: 'var(--navy)' }}>
                <Mail size={16} /> Weekly committee digest
              </h4>
              <p className="text-xs mt-1" style={{ color: 'var(--olive)' }}>
                Emails a short activity summary (top pages, new visitors, countries) to committee &amp; admin members
                {typeof digest.recipient_count === 'number' ? ` (${digest.recipient_count} recipient${digest.recipient_count === 1 ? '' : 's'})` : ''}.
                {digest.last_sent ? ` Last sent ${new Date(digest.last_sent).toLocaleString()}.` : ' Not sent yet.'}
              </p>
            </div>
            <button onClick={sendDigestNow} disabled={digestBusy}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm font-title"
              style={{ background: 'var(--navy)', color: 'var(--cream)', opacity: digestBusy ? 0.7 : 1 }} data-testid="digest-send-now">
              <Send size={15} /> {digestBusy ? 'Sending…' : 'Send now'}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-3">
            <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
              <input type="checkbox" checked={!!digest.enabled} onChange={(e) => saveDigest({ enabled: e.target.checked })} data-testid="digest-enabled" />
              Send automatically every week
            </label>
            <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
              on
              <select value={digest.send_day} onChange={(e) => saveDigest({ send_day: Number(e.target.value) })}
                disabled={!digest.enabled} className="border rounded px-2 py-1 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="digest-day">
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm flex-1 min-w-[220px]" style={{ color: 'var(--ink)' }}>
              Also send to
              <input value={digest.extra_recipients || ''} onChange={(e) => setDigest({ ...digest, extra_recipients: e.target.value })}
                onBlur={() => saveDigest({})} placeholder="extra emails, comma-separated"
                className="flex-1 border rounded px-2 py-1 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="digest-extra" />
            </label>
          </div>
          {digestMsg && (
            <p className="text-sm mt-2" data-testid="digest-msg" style={{ color: digestMsg.ok ? '#1c6b2c' : '#8a5a11' }}>{digestMsg.text}</p>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading…</p>
      ) : !summary ? (
        <p className="text-sm" style={{ color: '#8a1f11' }}>Could not load analytics.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <StatCard icon={Eye} label="Page views" value={summary.total_views} />
            <StatCard icon={Users} label="Unique visitors" value={summary.unique_visitors} />
            <StatCard icon={Globe} label="Countries" value={(summary.countries || []).filter((c) => c.label && c.label !== 'Unknown').length} />
            <StatCard icon={Monitor} label="Top page" value={(summary.top_pages && summary.top_pages[0] && summary.top_pages[0].label) || '—'} />
          </div>

          <MembersLocationMap />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
            <div className="lg:col-span-2">
              <VisitorMap locations={summary.locations || []} />
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="country-totals">
              <h4 className="font-title text-sm mb-3" style={{ color: 'var(--navy)' }}>Visitors by country</h4>
              {(() => {
                const countries = (summary.countries || []).filter((c) => c.label && c.label !== 'Unknown');
                if (countries.length === 0) return <p className="text-xs" style={{ color: 'var(--olive)' }}>No located visitors yet.</p>;
                const max = Math.max(1, ...countries.map((c) => c.count));
                return (
                  <div className="space-y-2">
                    {countries.map((c, i) => (
                      <div key={i} data-testid={`country-row-${i}`}>
                        <div className="flex justify-between text-xs mb-0.5" style={{ color: 'var(--ink)' }}>
                          <span className="truncate pr-2">{i + 1}. {c.label}</span>
                          <span style={{ color: 'var(--olive)' }}>{c.count}</span>
                        </div>
                        <div className="h-1.5 rounded" style={{ background: 'rgba(0,0,0,0.06)' }}>
                          <div className="h-1.5 rounded" style={{ width: `${(c.count / max) * 100}%`, background: 'var(--maroon)' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            <BreakdownList title="Top pages" items={summary.top_pages} />
            <BreakdownList title="Browsers" items={summary.browsers} />
            <BreakdownList title="Devices" items={summary.devices} />
            <BreakdownList title="Operating systems" items={summary.os} />
            <BreakdownList title="Countries" items={summary.countries} />
            <BreakdownList title="Referrers" items={summary.referrers} />
          </div>

          <h3 className="font-title text-lg mb-2" style={{ color: 'var(--navy)' }}>Recent activity</h3>
          <div className="overflow-x-auto bg-white rounded-lg shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <table className="w-full text-sm" data-testid="analytics-log-table">
              <thead>
                <tr style={{ background: 'var(--navy)' }}>
                  {['When', 'Page', 'Member', 'Browser', 'Device', 'Location'].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left font-title whitespace-nowrap" style={{ color: 'var(--cream)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(log && log.rows ? log.rows : []).map((r, i) => (
                  <tr key={i} style={{ background: i % 2 ? 'rgba(0,0,0,0.03)' : 'white' }}>
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2" data-testid={`analytics-log-page-${i}`}>{r.label ? <><span className="font-medium">{r.label}</span> <span className="text-xs" style={{ color: 'var(--olive)' }}>{r.path}</span></> : r.path}</td>
                    <td className="px-3 py-2">{r.email || <span style={{ color: 'var(--olive)' }}>Guest</span>}</td>
                    <td className="px-3 py-2">{r.browser}</td>
                    <td className="px-3 py-2">{r.device}</td>
                    <td className="px-3 py-2">{[r.city, r.country].filter(Boolean).join(', ') || <span style={{ color: 'var(--olive)' }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {log && log.total > (log.rows || []).length && (
              <p className="text-xs px-3 py-2" style={{ color: 'var(--olive)' }}>Showing the {(log.rows || []).length} most recent of {log.total} logged views. Export CSV for the full log.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
